import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { PageHeader } from "../components/Shared";
import FindingCard, { KIND } from "../components/packaging/FindingCard";
import ConfirmCorrectionsDialog from "../components/packaging/ConfirmCorrectionsDialog";
import UndoDialog from "../components/packaging/UndoDialog";

const isDecided = (f) => ["approved", "rejected"].includes(f.review?.status);

export default function PackagingReview() {
  const [params] = useSearchParams();
  const productId = params.get("product") || undefined;
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [kind, setKind] = useState("all");
  const [showDecided, setShowDecided] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // {findings, decision}
  const [undoing, setUndoing] = useState(null);

  const load = () => api.get("/packaging-review", { params: { product_id: productId, include_decided: showDecided } }).then((r) => { setData(r.data); setSelected(new Set()); }).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, [productId, showDecided]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = useMemo(() => (data?.items || []).filter((f) => isDecided(f) || kind === "all" || f.kind === kind), [data, kind]);
  const open = useMemo(() => items.filter((f) => !isDecided(f)), [items]);
  const byProduct = useMemo(() => {
    const m = new Map();
    items.forEach((f) => { if (!m.has(f.product_id)) m.set(f.product_id, { id: f.product_id, name: f.product_description, items: [] }); m.get(f.product_id).items.push(f); });
    return [...m.values()];
  }, [items]);

  const apply = async (findings, decision) => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/packaging-review/decide", { line_ids: findings.map((f) => f.line_id), decision });
      const newUnits = findings.filter((f) => f.kind === "new_purchase_unit").length;
      toast.success(decision === "approved"
        ? newUnits ? `${newUnits} purchase-unit definition${newUnits === 1 ? "" : "s"} confirmed on the product${r.decided > newUnits ? `, ${r.decided - newUnits} line${r.decided - newUnits === 1 ? "" : "s"} corrected` : ""} — undo any time from “Show decided”` : `${r.decided} line${r.decided === 1 ? "" : "s"} corrected — original packaging kept, undo any time from “Show decided”`
        : `${r.decided} line${r.decided === 1 ? "" : "s"} kept as verified history`);
      setConfirm(null);
      await load();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const undo = async (f) => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/packaging-review/undo", { line_ids: [f.line_id] });
      toast.success(r.restored ? "Correction undone — original invoice packaging restored" : "Line reopened for review");
      setUndoing(null);
      await load();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const toggle = (id, on) => setSelected((prev) => { const s = new Set(prev); on ? s.add(id) : s.delete(id); return s; });
  const selectKind = (k) => setSelected(new Set(open.filter((f) => f.kind === k).map((f) => f.line_id)));
  const review = (findings, decision) => setConfirm({ findings, decision });
  const selectedFindings = open.filter((f) => selected.has(f.line_id));

  return (
    <div data-testid="packaging-review-page">
      <Link to={productId ? `/products/${productId}` : "/products"} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-2" data-testid="packaging-review-back"><ArrowLeft className="h-4 w-4" /> {productId ? "Back to product" : "Back to products"}</Link>
      <PageHeader title="Packaging review" subtitle="Invoice lines whose stored packaging contradicts the product's confirmed packaging. Nothing changes until you confirm it; a correction updates only derived unit quantities and prices — never the invoice itself — and can be undone." />
      {!data ? <p className="text-sm text-slate-500">Checking…</p> : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="packaging-review-summary">
            <button onClick={() => setKind("all")} className={`rounded-full px-3 py-1 text-xs font-semibold border ${kind === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`} data-testid="filter-all">All {data.count}</button>
            {Object.entries(KIND).map(([k, v]) => <button key={k} onClick={() => setKind(k)} className={`rounded-full px-3 py-1 text-xs font-semibold border ${kind === k ? "ring-2 ring-slate-900 " : ""}${v.cls}`} data-testid={`filter-${k}`}>{v.label} {data.by_kind?.[k] || 0}</button>)}
            <label className="ml-auto inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="show-decided-toggle">
              <Switch checked={showDecided} onCheckedChange={setShowDecided} data-testid="show-decided-switch" /> Show decided{showDecided && data.decided != null ? ` (${data.decided})` : ""}
            </label>
          </div>
          {kind !== "all" && <p className="text-xs text-slate-500 mb-3" data-testid="kind-hint">{KIND[kind].hint}</p>}
          {open.length > 0 && (
            <div className="sticky top-0 z-10 mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white/95 backdrop-blur px-3 py-2" data-testid="bulk-bar">
              <span className="text-sm text-slate-700"><strong data-testid="selected-count">{selected.size}</strong> selected</span>
              <Button size="sm" variant="outline" onClick={() => selectKind("stale_extraction")} data-testid="select-stale">Select all likely read errors</Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} disabled={!selected.size} data-testid="select-none">Clear</Button>
              <div className="ml-auto flex gap-2">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={!selected.size || busy} onClick={() => review(selectedFindings, "approved")} data-testid="bulk-approve"><Check className="h-4 w-4 mr-1" /> Approve selected…</Button>
                <Button size="sm" variant="outline" disabled={!selected.size || busy} onClick={() => review(selectedFindings, "rejected")} data-testid="bulk-reject">Keep selected as is…</Button>
              </div>
            </div>
          )}
          {items.length === 0 && <p className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-center gap-2" data-testid="packaging-review-empty"><ShieldAlert className="h-4 w-4" /> Nothing to review — every invoice line agrees with its product's packaging (or was already decided).</p>}
          <div className="space-y-6">
            {byProduct.map((p) => (
              <section key={p.id} data-testid={`review-product-${p.id}`}>
                <h2 className="text-sm font-semibold text-slate-900 mb-2"><Link to={`/products/${p.id}`} className="hover:text-emerald-700">{p.name}</Link> <span className="text-slate-400 font-normal">· {p.items.length} line{p.items.length === 1 ? "" : "s"}</span></h2>
                <ul className="space-y-2">{p.items.map((f) => <FindingCard key={f.line_id} f={f} selected={selected.has(f.line_id)} onSelect={toggle} onDecide={review} onUndo={setUndoing} busy={busy} />)}</ul>
              </section>
            ))}
          </div>
        </>
      )}
      <ConfirmCorrectionsDialog open={!!confirm} findings={confirm?.findings || []} decision={confirm?.decision} onClose={() => setConfirm(null)} onConfirm={(chosen) => apply(chosen, confirm.decision)} busy={busy} />
      <UndoDialog finding={undoing} onClose={() => setUndoing(null)} onConfirm={undo} busy={busy} />
    </div>
  );
}
